import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Mail, ShieldCheck } from "lucide-react";
import { api, ApiError } from "../api";
import { useAuth } from "../auth";
import { Button, Field } from "../ui";
import { PasswordInput } from "../components/PasswordInput";

const OAUTH_ERROR_COPY: Record<string, string> = {
  no_account:
    "We couldn't find an account for that Google address. Ask Property Management for an invite, then try again.",
  invalid_state: "Sign-in expired or was tampered with. Please try again.",
  token_exchange: "Google didn't accept the sign-in. Please try again.",
  userinfo: "Couldn't read your Google profile. Please try again.",
  no_email: "Your Google account didn't share a verified email.",
  inactive: "This account is deactivated. Contact Property Management.",
  google_not_configured: "Google sign-in isn't enabled on this server.",
  internal: "Sign-in failed. Please try again.",
};

export function LoginPage() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [failedAttempt, setFailedAttempt] = useState(false);

  // MFA second-step state. When the server returns mfa_required on
  // /auth/login, we hold its short-lived challengeToken and ask the
  // user for a TOTP code (or a backup code) before letting it grant
  // a session.
  const [mfa, setMfa] = useState<{ token: string } | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaUseBackup, setMfaUseBackup] = useState(false);

  // Probe whether the server has Google OAuth configured. If not, hide
  // the button rather than show a button that always errors.
  useEffect(() => {
    void api
      .get<{ google: boolean; propertyManagerEmail: string | null }>(
        "/auth/providers",
      )
      .then((r) => setGoogleEnabled(Boolean(r.google)))
      .catch(() => setGoogleEnabled(false));
  }, []);

  // Surface OAuth-callback errors that arrive via ?oauth_error=...
  useEffect(() => {
    const oauthError = searchParams.get("oauth_error");
    if (oauthError) {
      setError(OAUTH_ERROR_COPY[oauthError] ?? "Google sign-in failed.");
    }
  }, [searchParams]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await api.post<
        | { ok: true }
        | { mfa_required: true; challengeToken: string; expiresAt: string }
      >("/auth/login", { email, password });
      if ("mfa_required" in r && r.mfa_required) {
        setMfa({ token: r.challengeToken });
        setMfaCode("");
        setMfaUseBackup(false);
        return;
      }
      await refresh();
      nav("/projects", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const wrong = err.status === 401;
        setFailedAttempt(wrong);
        setError(wrong ? "Email or password is incorrect." : "Sign-in failed.");
      } else setError("Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const onSubmitMfa = async (e: FormEvent) => {
    e.preventDefault();
    if (!mfa) return;
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/login/mfa", {
        challengeToken: mfa.token,
        ...(mfaUseBackup
          ? { backupCode: mfaCode.trim() }
          : { code: mfaCode.trim() }),
      });
      await refresh();
      nav("/projects", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string })?.error;
        setError(
          code === "mfa_invalid"
            ? mfaUseBackup
              ? "That backup code didn't match or has already been used."
              : "That authenticator code didn't match. Try the next one."
            : code === "challenge_invalid"
              ? "Your sign-in expired. Please enter your password again."
              : "Sign-in failed.",
        );
        if (code === "challenge_invalid") {
          setMfa(null);
          setMfaCode("");
          setPassword("");
        }
      } else setError("Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const cancelMfa = () => {
    setMfa(null);
    setMfaCode("");
    setMfaUseBackup(false);
    setError(null);
    setPassword("");
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center -mt-6 -mx-4 sm:-mx-6 lg:-mx-8 py-10 px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-ink-800">Welcome back</h1>
          <p className="mt-1.5 text-ink-500 font-light">
            Sign in to manage your construction projects.
          </p>
        </div>
        <div className="card">
          {mfa ? (
            <form onSubmit={onSubmitMfa} className="space-y-1">
              <div className="mb-3 inline-flex items-center gap-2 rounded-md bg-status-approved-bg px-2.5 py-1 text-xs text-status-approved-fg">
                <ShieldCheck size={14} />
                Two-factor required
              </div>
              <p className="text-sm text-ink-700 mb-3">
                Enter the {mfaUseBackup ? "backup" : "6-digit"} code from your
                authenticator app to finish signing in.
              </p>
              <Field
                label={mfaUseBackup ? "Backup code" : "Authenticator code"}
              >
                <input
                  className="input"
                  inputMode={mfaUseBackup ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  pattern={mfaUseBackup ? undefined : "[0-9]*"}
                  maxLength={mfaUseBackup ? 12 : 6}
                  value={mfaCode}
                  onChange={(e) => {
                    const v = mfaUseBackup
                      ? e.target.value.toUpperCase()
                      : e.target.value.replace(/\D/g, "");
                    setMfaCode(v);
                  }}
                  required
                  autoFocus
                />
              </Field>
              {error && <div className="alert-error">{error}</div>}
              <Button
                variant="primary"
                type="submit"
                className="w-full mt-2"
                loading={busy}
                disabled={busy || mfaCode.length < (mfaUseBackup ? 6 : 6)}
              >
                {busy ? "Verifying…" : "Verify and sign in"}
                {!busy && <ArrowRight size={16} />}
              </Button>
              <div className="mt-3 flex items-center justify-between text-xs text-ink-500">
                <button
                  type="button"
                  className="text-brand-700 hover:text-brand-800 bg-transparent border-0 p-0 cursor-pointer"
                  onClick={() => {
                    setMfaUseBackup((b) => !b);
                    setMfaCode("");
                    setError(null);
                  }}
                >
                  {mfaUseBackup
                    ? "Use authenticator code instead"
                    : "Use a backup code instead"}
                </button>
                <button
                  type="button"
                  className="text-ink-500 hover:text-ink-700 bg-transparent border-0 p-0 cursor-pointer"
                  onClick={cancelMfa}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={onSubmit} className="space-y-1">
            <Field label="Email">
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                />
                <input
                  className="input pl-9"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="you@example.com"
                />
              </div>
            </Field>
            <Field label="Password">
              <PasswordInput
                value={password}
                onChange={setPassword}
                showLockGlyph
                autoComplete="current-password"
                required
              />
            </Field>
            {error && (
              <div className="alert-error">
                {error}
                {failedAttempt && (
                  <>
                    {" "}
                    Need a reset?{" "}
                    <Link
                      to="/forgot-password"
                      className="font-medium text-brand-700 hover:text-brand-800"
                    >
                      Reset your password
                    </Link>
                    .
                  </>
                )}
              </div>
            )}
            <Button
              variant="primary"
              type="submit"
              className="w-full mt-2"
              loading={busy}
            >
              {busy ? "Signing in…" : "Sign in"}
              {!busy && <ArrowRight size={16} />}
            </Button>
            <p className="mt-2 text-center text-xs text-ink-500">
              <Link
                to="/forgot-password"
                className="font-medium text-brand-700 hover:text-brand-800"
              >
                Forgot your password?
              </Link>
              <span className="mx-1.5 text-ink-300">·</span>
              <Link
                to="/login-link"
                className="font-medium text-brand-700 hover:text-brand-800"
              >
                Email me a sign-in link
              </Link>
            </p>
          </form>
          )}
          {!mfa && googleEnabled && (
            <>
              <div className="my-4 flex items-center gap-3 text-xs text-ink-400">
                <span className="h-px flex-1 bg-ink-100" />
                <span>or</span>
                <span className="h-px flex-1 bg-ink-100" />
              </div>
              <a
                href="/auth/google/start"
                className="flex w-full items-center justify-center gap-2 rounded-md border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 no-underline shadow-soft transition-colors hover:bg-sand-50 hover:no-underline"
              >
                <GoogleGlyph />
                Continue with Google
              </a>
            </>
          )}
        </div>
        <p className="mt-4 text-center text-sm text-ink-500">
          New here?{" "}
          <Link
            to="/register"
            className="font-medium text-brand-700 hover:text-brand-800"
          >
            Register with an invite code
          </Link>
          .
        </p>
        <p className="mt-1 text-center text-sm text-ink-500">
          Just bought a unit?{" "}
          <Link
            to="/claim-unit"
            className="font-medium text-brand-700 hover:text-brand-800"
          >
            Claim it here
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4c-7.5 0-14 4.1-17.7 10.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.9 39.8 16.4 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2c-.4.4 6.8-5 6.8-14.8 0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
